import { Box, IconButton, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import BasicDialog from "../../../../common/components/functional/dialogs/BasicDialog";
import { ReactComponent as EditIcon } from "../../../../common/icons/editGray.svg";
import { NameIdOption } from "../../../../common/types/commonTypes";
import { Nullable } from "../../../../common/types/nullable";
import { fetchCustomerCompanyById } from "../../../customer/customerOrganizationsSlice";
import { Talent, talentIsDeactivated } from "../../../talents/talentAPI";
import { setTalentRecruiterCompany } from "../../../talents/talentsSlice";
import { canExpertEditTalentProfile } from "../../../user/accessRightChecks";
import TalentSummaryCard from "../TalentSummaryCard";
import TalentCustomerRecruitDialogContent from "./TalentCustomerRecruitDialogContent";

type Props = {
  talent: Talent;
};

const TalentCustomerRecruit = ({ talent }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  // recruitedByCustomerCompany with fetched name property
  const [initialRecruitedCompany, setInitialRecruitedCompany] =
    useState<Nullable<NameIdOption>>(null);
  const [newRecruitedCompany, setNewRecruitedCompany] = useState<
    Nullable<NameIdOption> | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (talent?.recruitedByCustomerCompany) {
      // fetch name for the initial recruitedByCustomerCompany (id)
      setIsLoading(true);
      dispatch(
        fetchCustomerCompanyById({ id: talent?.recruitedByCustomerCompany }),
      )
        .then((action) => {
          if (isFulfilled(action)) {
            setInitialRecruitedCompany({
              id: action.payload.id,
              name: action.payload.name,
            });
          }
        })
        .finally(() => setIsLoading(false));
    }
    // no need to fetch again if talent is changed, name is already stored
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const onChange = (customer: Nullable<NameIdOption>) =>
    setNewRecruitedCompany(customer);

  const resetDialogState = () => {
    setNewRecruitedCompany(undefined);
    setIsDirty(false);
  };

  useEffect(() => {
    if (newRecruitedCompany !== undefined) {
      const hasChanged =
        (newRecruitedCompany?.id || null) !==
        (initialRecruitedCompany?.id || null);
      setIsDirty(hasChanged);
    } else {
      setIsDirty(false);
    }
  }, [newRecruitedCompany, initialRecruitedCompany]);

  const onConfirm = () => {
    dispatch(
      setTalentRecruiterCompany({
        talentId: talent.id,
        ...(newRecruitedCompany?.id && {
          customerCompanyId: newRecruitedCompany.id,
        }),
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setDialogOpen(false);
        // update the initial recruited company on successful save
        setInitialRecruitedCompany(newRecruitedCompany || null);
        // reset state
        resetDialogState();
      }
    });
  };

  const renderEmptyState = !isLoading && !initialRecruitedCompany?.name;

  return (
    <TalentSummaryCard title={t("profileSummary.customerRecruit")} divider>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography>
          {renderEmptyState
            ? t("profileSummary.notRecruitedByCustomer")
            : initialRecruitedCompany?.name || ""}
        </Typography>
        <IconButton
          disabled={!canExpertEditTalentProfile || talentIsDeactivated(talent)}
          onClick={() => {
            resetDialogState();
            setDialogOpen(true);
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
      <BasicDialog
        open={dialogOpen}
        onCancel={() => {
          resetDialogState();
          setDialogOpen(false);
        }}
        title={t("profileSummary.setRecruitedCustomer")}
        onConfirm={onConfirm}
        confirmDisabled={!isDirty}
        content={
          <TalentCustomerRecruitDialogContent
            onChange={onChange}
            newRecruitedCompany={newRecruitedCompany}
            initialRecruitedCompany={initialRecruitedCompany}
          />
        }
      />
    </TalentSummaryCard>
  );
};

export default TalentCustomerRecruit;
